import { getAgentDisplayName, resolveAgentVisuals, resolveDefaultRoleLine } from '@tunasong/plugin-agent/integration'
import type { Agent, ColorName, Persisted } from '@tunasong/schemas'
import { Avatar, getColorCSS, Typography } from '@tunasong/ui-lib'
import type { CSSProperties, FC } from 'react'
import styles from './agent-identity-header.module.css'

export interface AgentIdentityHeaderProps {
  agent: Persisted<Agent>
  /** ChatBox id below — the roleLine renders `${composerId}-role-description` as its id. */
  composerId: string
  /** False when the tabs row already names the active agent (2+ agents). */
  showName: boolean
}

/**
 * Persistent identity header for the Agents tool (Joni spec §1). Renders on
 * EVERY thread state — empty or not — so the agent's identity and the
 * aria-describedby anchor never disappear. The empty-state starter chips live
 * separately in AgentChatEmptyHeader.
 */
export const AgentIdentityHeader: FC<AgentIdentityHeaderProps> = props => {
  const { agent, composerId, showName } = props
  const { color, Icon } = resolveAgentVisuals(agent)
  const accent = getColorCSS(color as ColorName)
  const roleLine = resolveDefaultRoleLine(agent)

  return (
    <div
      className={styles.container}
      style={
        {
          '--agent-accent': accent,
          '--agent-accent-header-bg': 'color-mix(in srgb, var(--agent-accent) 6%, transparent)',
        } as CSSProperties
      }
    >
      <Avatar className={styles.avatar}>
        <Icon aria-hidden="true" />
      </Avatar>
      <div className={styles.text}>
        {showName ? (
          <Typography variant="subtitle2" className={styles.name}>
            {getAgentDisplayName(agent)}
          </Typography>
        ) : null}
        <Typography variant="caption" component="p" className={styles.roleLine} id={`${composerId}-role-description`}>
          {roleLine}
        </Typography>
      </div>
    </div>
  )
}
